import React, { useEffect, useState, useCallback } from 'react';
import { LoadingAnimation } from './ui/LoadingAnimation';
import { detectIsIOS } from '../lib/deviceDetection';

interface GoogleLoginProps {
  onSuccess: (credential: string) => void;
  onError: (error: string) => void;
}

declare global {
  interface Window {
    google: any;
    googleLoginCallback: (response: any) => void;
  }
}

// Helper used in tests and initialization to determine FedCM usage
export function shouldUseFedCM(hostname: string, userAgent: string): boolean {
  const isSafariUA = /safari/i.test(userAgent) && !/chrome|android/i.test(userAgent);
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const isBraveUA = /brave/i.test(userAgent);
  return !(isSafariUA || isLocalhost || isBraveUA);
}

export { detectIsIOS };

// Google domains used during the OAuth account selection flow
const GOOGLE_OAUTH_DOMAINS = [
  'https://accounts.google.com',
  'https://ssl.gstatic.com',
  'https://www.gstatic.com',
  'https://apis.google.com',
  'https://lh3.googleusercontent.com',
  'https://oauth2.googleapis.com',
];

// Pre-warm TCP+TLS connections to Google's OAuth servers.
// Refreshes preconnect hints (which may have timed out since initial page load)
// and fires lightweight fetches to fully establish the connection pipeline.
// Throttled to at most once every 30 seconds to avoid excessive requests.
let _lastWarmTime = 0;
export function warmGoogleOAuthConnections(): void {
  const now = Date.now();
  if (now - _lastWarmTime < 30_000) return;
  _lastWarmTime = now;

  GOOGLE_OAUTH_DOMAINS.forEach(href => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = href;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });

  fetch('https://accounts.google.com/gsi/style', { mode: 'no-cors' }).catch(() => {});
  fetch('https://accounts.google.com/gsi/client', { mode: 'no-cors' }).catch(() => {});
}

export const GoogleLogin: React.FC<GoogleLoginProps> = ({ onSuccess, onError }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const buttonRef = React.useRef<HTMLDivElement>(null);
  const isSafari = typeof window !== 'undefined' && /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isBrave = typeof navigator !== 'undefined' && ((navigator as any).brave || /brave/i.test(navigator.userAgent));
  const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const isIOS = detectIsIOS();

  const scriptInjectedByUs = React.useRef(false);

  // On iOS/Safari, warm connections immediately on mount so the popup opens faster
  useEffect(() => {
    if (isIOS || isSafari) {
      warmGoogleOAuthConnections();
    }
  }, [isIOS, isSafari]);

  // On hover/touchstart, warm connections for all browsers (handles timeout since page load)
  const handleWarmup = useCallback(() => {
    warmGoogleOAuthConnections();
  }, []);

  useEffect(() => {
    const GSI_URL = 'https://accounts.google.com/gsi/client';
    const existingScript = document.querySelector(`script[src="${GSI_URL}"]`);

    const markLoaded = () => {
      console.log('[GSI-Timing] Script ready at', Math.round(performance.now()), 'ms');
      setScriptLoaded(true);
    };

    if (existingScript) {
      // Script already in page (e.g. from index.html preload) – wait for window.google
      if (window.google?.accounts?.id) {
        markLoaded();
        return () => {
          try {
            if (window.google?.accounts?.id) window.google.accounts.id.cancel();
          } catch (_) {}
        };
      }
      const onLoad = () => {
        existingScript.removeEventListener('load', onLoad);
        markLoaded();
      };
      existingScript.addEventListener('load', onLoad);
      // In case it loaded before we attached
      if (window.google?.accounts?.id) markLoaded();
      const poll = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(poll);
          markLoaded();
        }
      }, 50);
      const timeout = setTimeout(() => {
        clearInterval(poll);
        if (!window.google?.accounts?.id) markLoaded(); // allow UI to show and retry
      }, 8000);
      return () => {
        clearInterval(poll);
        clearTimeout(timeout);
        existingScript.removeEventListener('load', onLoad);
        try {
          if (window.google?.accounts?.id) window.google.accounts.id.cancel();
        } catch (_) {}
      };
    }

    // No script in page – inject it (e.g. direct navigation to /login)
    scriptInjectedByUs.current = true;
    const script = document.createElement('script');
    script.src = GSI_URL;
    script.async = true;
    script.defer = true;
    script.onload = markLoaded;
    script.onerror = () => {
      if (process.env.NODE_ENV === 'development') console.error('Failed to load Google script');
      onError('Failed to load Google Sign-In script');
    };
    document.head.appendChild(script);

    return () => {
      try {
        if (window.google?.accounts?.id) window.google.accounts.id.cancel();
      } catch (_) {}
      if (scriptInjectedByUs.current) {
        const s = document.querySelector(`script[src="${GSI_URL}"]`);
        if (s) document.head.removeChild(s);
        scriptInjectedByUs.current = false;
      }
    };
  }, [isSafari]);

  const initializeGoogleSignIn = () => {
    console.log('[GSI-Timing] Initializing at', Math.round(performance.now()), 'ms');
    if (window.google && buttonRef.current) {
      // Suppress console.log of client ID in production
      if (process.env.NODE_ENV === 'development') {
        console.log('Google Client ID:', process.env.REACT_APP_GOOGLE_CLIENT_ID);
      }
      
      try {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
          ux_mode: 'popup',
          use_fedcm_for_prompt: false,
          itp_support: isSafari,
        });

        // Always render the Google-provided button (works across browsers with popup UX)
        if (buttonRef.current) {
          const measuredWidth = Math.max(
            Math.round(buttonRef.current.getBoundingClientRect().width || buttonRef.current.offsetWidth || 0),
            280
          );
          buttonRef.current.innerHTML = '';

          // Render the sign-in button with error suppression (non-Safari only)
          window.google.accounts.id.renderButton(buttonRef.current, {
            theme: 'outline',
            size: 'large',
            text: 'continue_with',
            shape: 'rectangular',
            width: measuredWidth,
          });
        }

        // console.log('Google Sign-In initialized successfully');
      } catch (error) {
        // Suppress non-critical Google initialization errors (like origin validation)
        // These don't prevent login from working
        if (process.env.NODE_ENV === 'development') {
          console.warn('Google Sign-In initialization error (non-critical):', error);
        }
      }
    } else {
      // console.log('Waiting for Google object or button ref...');
    }
  };

  // Initialize as soon as script is loaded and button ref is ready (no delay)
  useEffect(() => {
    if (scriptLoaded && buttonRef.current) {
      initializeGoogleSignIn();
    }
  }, [scriptLoaded, isSafari]);

  const handleCredentialResponse = (response: any) => {
    console.log('[GSI-Timing] Credential received at', Math.round(performance.now()), 'ms');
    setIsLoading(false);
    if (response.credential) {
      onSuccess(response.credential);
    } else {
      console.error('No credential in response');
      onError('Failed to get Google credential');
    }
  };

  const handleGoogleLogin = () => {
    console.log('[GSI-Timing] Login button clicked at', Math.round(performance.now()), 'ms');
    warmGoogleOAuthConnections();
    if (!scriptLoaded || !window.google) {
      console.error('Google Sign-In not loaded');
      onError('Google Sign-In not loaded');
      return;
    }

    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      console.error('Google Client ID not configured');
      onError('Google Client ID not configured');
      return;
    }

    setIsLoading(true);
    try {
      // Provide a moment callback so we can handle failures and stop the spinner
      window.google.accounts.id.prompt((notification: any) => {
        try {
          const getMomentType = notification?.getMomentType?.();
          console.log('[GSI-Timing] Prompt moment:', getMomentType, 'at', Math.round(performance.now()), 'ms');
          const notDisplayed = notification?.isNotDisplayed?.();
          const skipped = notification?.isSkippedMoment?.();
          const notDisplayedReason = notification?.getNotDisplayedReason?.();
          const skippedReason = notification?.getSkippedReason?.();

          if (notDisplayed || skipped) {
            setIsLoading(false);
            if (notDisplayedReason === 'origin_mismatch' || notDisplayedReason === 'unregistered_origin') {
              onError(`Google Sign-In origin not allowed for client. Add ${window.location.origin} to Authorized JavaScript origins.`);
              return;
            }
            if (notDisplayedReason === 'third_party_cookies_blocked' || notDisplayedReason === 'browser_not_supported') {
              onError('Browser blocked Google Sign-In. Enable third-party cookies or try another browser.');
              return;
            }
            if (process.env.NODE_ENV === 'development') {
              // Helpful diagnostics during local development
              console.warn('GSI not displayed/skipped', {
                reason: notDisplayedReason || skippedReason,
                origin: window.location.origin,
                hostname: window.location.hostname,
                useFedCM: shouldUseFedCM(window.location.hostname, navigator.userAgent),
                isSafari,
                isBrave,
                isLocalhost
              });
            }
            if (notDisplayedReason) {
              onError(`Google Sign-In not displayed: ${notDisplayedReason}`);
              return;
            }
            if (skippedReason) {
              onError(`Google Sign-In skipped: ${skippedReason}`);
              return;
            }
            onError('Google Sign-In closed or not displayed');
          }

          // If displayed, keep spinner until credential callback fires
          if (getMomentType === 'display') {
            // No-op
          }
        } catch (e) {
          setIsLoading(false);
          onError('Google Sign-In failed to open');
        }
      });
    } catch (error) {
      console.error('Error triggering Google Sign-In:', error);
      setIsLoading(false);
      onError('Failed to trigger Google Sign-In');
    }
  };

  if (!scriptLoaded) {
    return (
      <div className="sso-provider-shell w-full rounded-xl bg-gray-100 p-4 flex items-center justify-center">
        <div className="flex items-center gap-2">
          <LoadingAnimation size={16} />
          <span className="text-gray-600">Loading Google Sign-In...</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="sso-provider-shell w-full"
      onMouseEnter={handleWarmup}
      onTouchStart={handleWarmup}
      onFocus={handleWarmup}
    >
      {/* Google-rendered button */}
      <div ref={buttonRef} className="google-login-wrapper w-full" style={{ display: 'block' }} />
      
      {/* Custom button for Safari or fallback */}
      <button
        onClick={handleGoogleLogin}
        disabled={isLoading}
        className="w-full bg-white hover:bg-gray-50 text-gray-900 border border-gray-300 shadow-sm inline-flex items-center justify-center rounded-xl font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none h-11 px-4 py-2"
        style={{ display: 'none' }}
      >
        {isLoading ? (
          <div
            className="grid w-full grid-cols-[20px_minmax(0,1fr)_20px] items-center gap-3"
            data-testid="google-login-fallback-content"
          >
            <span className="flex h-5 w-5 items-center justify-center" data-testid="google-login-fallback-icon-slot">
              <LoadingAnimation size={16} />
            </span>
            <span className="text-center">Signing in...</span>
            <span aria-hidden="true" />
          </div>
        ) : (
          <div
            className="grid w-full grid-cols-[20px_minmax(0,1fr)_20px] items-center gap-3"
            data-testid="google-login-fallback-content"
          >
            <span
              className="flex h-5 w-5 items-center justify-center"
              data-testid="google-login-fallback-icon-slot"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            </span>
            <span className="text-center">Continue with Google</span>
            <span aria-hidden="true" />
          </div>
        )}
      </button>
    </div>
  );
}; 